import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../Button/Button';
import { XIcon } from '../../Icons/Icons';
import type { CriterionData, CriterionWeight } from '../../../types';
import './WeightConfigModal.css';

interface WeightConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  criteria: CriterionData[];
  initialWeights?: CriterionWeight[];
  onSave: (weights: CriterionWeight[]) => void;
}

export const WeightConfigModal: React.FC<WeightConfigModalProps> = ({
  isOpen,
  onClose,
  criteria,
  initialWeights = [],
  onSave
}) => {
  const { t } = useTranslation();
  const [weights, setWeights] = useState<CriterionWeight[]>([]);

  useEffect(() => {
    if (isOpen) {
      // 初期化：既存の重みがあればそれを使用、なければデフォルト値を設定
      const initializedWeights = criteria.map((_, index) => {
        const existingWeight = initialWeights.find(w => w.criterionIndex === index);
        return existingWeight || {
          criterionIndex: index,
          weight: 100 / criteria.length, // 均等配分
          maxPoints: 100
        };
      });
      setWeights(initializedWeights);
    }
  }, [isOpen, criteria, initialWeights]);

  const handleWeightChange = (criterionIndex: number, weight: number) => {
    setWeights(prev => 
      prev.map(w => 
        w.criterionIndex === criterionIndex 
          ? { ...w, weight: Math.max(0, Math.min(100, weight)) }
          : w
      )
    );
  };

  const handleMaxPointsChange = (criterionIndex: number, maxPoints: number) => {
    setWeights(prev =>
      prev.map(w =>
        w.criterionIndex === criterionIndex
          ? { ...w, maxPoints: Math.max(1, maxPoints) }
          : w
      )
    );
  };

  const handleEqualDistribution = () => {
    const equalWeight = 100 / criteria.length;
    setWeights(prev =>
      prev.map(w => ({ ...w, weight: equalWeight }))
    );
  };

  const handleSave = () => {
    // 重みの合計が100%になるように正規化
    const totalWeight = weights.reduce((sum, w) => sum + w.weight, 0);
    const normalizedWeights = weights.map(w => ({
      ...w,
      weight: totalWeight > 0 ? (w.weight / totalWeight) * 100 : 100 / weights.length
    }));
    
    onSave(normalizedWeights);
    onClose();
  };

  const totalWeight = weights.reduce((sum, w) => sum + w.weight, 0);

  if (!isOpen) return null;

  return (
    <div className="weight-config-modal-overlay">
      <div className="weight-config-modal">
        <div className="weight-config-modal__header">
          <h2>{t('weight.title')}</h2>
          <button 
            className="weight-config-modal__close-button"
            onClick={onClose}
            aria-label={t('common.close')}
          >
            <XIcon />
          </button>
        </div>

        <div className="weight-config-modal__content">
          <div className="weight-config-modal__description">
            {t('weight.description')}
          </div>

          <div className="weight-config-modal__actions">
            <Button
              variant="secondary"
              size="sm"
              onClick={handleEqualDistribution}
            >
              {t('weight.equalDistribution')}
            </Button>
            <div className="weight-total">
              {t('weight.currentTotal')} <span className={totalWeight > 105 || totalWeight < 95 ? 'weight-warning' : 'weight-ok'}>
                {totalWeight.toFixed(1)}%
              </span>
            </div>
          </div>

          <div className="weight-config-table">
            <div className="weight-config-table__header">
              <div>{t('weight.criterion')}</div>
              <div>{t('weight.weight')}</div>
              <div>{t('weight.maxScore')}</div>
            </div>
            
            {criteria.map((criterion, index) => {
              const weight = weights.find(w => w.criterionIndex === index);
              return (
                <div key={index} className="weight-config-table__row">
                  <div className="criterion-name">
                    <span className="criterion-title">{criterion.title}</span>
                    <div className="criterion-description">
                      {criterion.description.slice(0, 2).join(', ')}
                      {criterion.description.length > 2 && '...'}
                    </div>
                  </div>
                  
                  <div className="weight-input-container">
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.1"
                      value={weight?.weight.toFixed(1) || '0'}
                      onChange={(e) => handleWeightChange(index, parseFloat(e.target.value) || 0)}
                      className="weight-input"
                    />
                  </div>
                  
                  <div className="max-points-input-container">
                    <input
                      type="number"
                      min="1"
                      step="1"
                      value={weight?.maxPoints || 100}
                      onChange={(e) => handleMaxPointsChange(index, parseInt(e.target.value) || 100)}
                      className="max-points-input"
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="weight-config-modal__footer">
          <Button
            variant="secondary"
            onClick={onClose}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={weights.length === 0}
          >
            {t('weight.saveSettings')}
          </Button>
        </div>
      </div>
    </div>
  );
};
